import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { headerRoutes } from "../common/constants";
import { brandGradientButtonStyles } from "../../lib/brandGradient";
import { Link as ScrollLink, scroller } from "react-scroll";
import MobileMenu from "../modal/Menu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RiArrowDownSLine, RiMenu3Fill } from "react-icons/ri";
import { useBizflexAppUrls } from "../../utils/useBizflexAppUrls";

const MotionBox = motion(Box);

const dropdownPanelVariants = {
  hidden: { opacity: 0, y: -10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.98,
    transition: { duration: 0.16, ease: "easeIn" },
  },
};

const dropdownItemVariants = {
  hidden: { opacity: 0, y: -6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
};

const FeaturesDropdown = ({ item, useLightNav }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const navColor = useLightNav ? "#dcdcdc" : "dark";

  return (
    <Box ref={dropdownRef} position="relative" zIndex={20}>
      <Flex
        as="button"
        type="button"
        align="center"
        gap="4px"
        bg="transparent"
        border="none"
        p={0}
        cursor="pointer"
        color={navColor}
        fontSize="12px"
        fontWeight={400}
        lineHeight="1"
        whiteSpace="nowrap"
        transition=".3s ease-in-out"
        _hover={{ color: "orange" }}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Text as="span" lineHeight="1">
          {item.name}
        </Text>
        <Flex
          as="span"
          align="center"
          justify="center"
          h="12px"
          w="12px"
          flexShrink={0}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s ease"
        >
          <RiArrowDownSLine size="14px" />
        </Flex>
      </Flex>

      <AnimatePresence>
        {isOpen ? (
          <MotionBox
            position="absolute"
            top="calc(100% + 12px)"
            left="50%"
            style={{ translateX: "-50%" }}
            bg="#fff"
            borderRadius="16px"
            boxShadow="0 12px 32px rgba(15, 23, 43, 0.14)"
            p="10px"
            minW="420px"
            w="max-content"
            zIndex={1500}
            variants={dropdownPanelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {item.children.map((child) => (
              <MotionBox
                key={child.name}
                variants={dropdownItemVariants}
              >
                <Box
                  as={Link}
                  to={child.path}
                  display="flex"
                  alignItems="center"
                  gap="10px"
                  px="10px"
                  py="10px"
                  borderRadius="12px"
                  textDecoration="none"
                  cursor="pointer"
                  transition="background-color 0.2s ease"
                  _hover={{ bg: "#F5F7FA" }}
                  onClick={() => setIsOpen(false)}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg="#EEF4FF"
                    flexShrink={0}
                  >
                    <Image
                      src={child.icon}
                      alt=""
                      w="16px"
                      h="16px"
                      objectFit="contain"
                    />
                  </Flex>
                  <Box minW={0}>
                    <Text
                      color="#0F172B"
                      fontSize="14px"
                      fontWeight={500}
                      lineHeight="1.3"
                      mb="2px"
                    >
                      {child.name}
                    </Text>
                    <Text
                      color="#64748B"
                      fontSize="10px"
                      lineHeight="1.4"
                      // fontWeight={400}
                      whiteSpace="nowrap"
                    >
                      {child.description}
                    </Text>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </MotionBox>
        ) : null}
      </AnimatePresence>
    </Box>
  );
};

const Header = ({ isActive, useDarkScrollBg = isActive, contentMaxW = "1326px" }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const { loginUrl, registerUrl } = useBizflexAppUrls();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleRouteNav = (path) => {
    navigate(path);
    scrollToTop();
  };

  const handleHomeSectionNav = (sectionId) => {
    if (pathname === "/") {
      scroller.scrollTo(sectionId, { smooth: true, duration: 500 });
      return;
    }

    navigate("/");
    window.setTimeout(() => {
      scroller.scrollTo(sectionId, { smooth: true, duration: 500 });
    }, 150);
  };

  const useLightNav = isActive || (useDarkScrollBg && isScrolled);

  const navTextStyles = {
    transition: ".3s ease-in-out",
    cursor: "pointer",
    _hover: { color: "orange" },
    color: useLightNav ? "#dcdcdc" : "dark",
    fontSize: "12px",
    style: { textDecoration: "none" },
  };

  const renderNavItem = (item) => {
    if (item.hasDropdown && item.children?.length) {
      return (
        <FeaturesDropdown
          key={item.id}
          item={item}
          useLightNav={useLightNav}
        />
      );
    }

    if (item.isExternal) {
      return (
        <Text key={item.id} as="a" href={item.path} {...navTextStyles}>
          {item.name}
        </Text>
      );
    }

    if (item.path && item.path !== "/") {
      return (
        <Text
          key={item.id}
          onClick={() => handleRouteNav(item.path)}
          {...navTextStyles}
        >
          {item.name}
        </Text>
      );
    }

    return (
      <ScrollLink key={item.id} to={item.id} smooth duration={500}>
        <Text
          onClick={() => handleHomeSectionNav(item.id)}
          {...navTextStyles}
        >
          {item.name}
        </Text>
      </ScrollLink>
    );
  };

  return (
    <Flex
      pos="fixed"
      top={0}
      left={0}
      right={0}
      w="100%"
      zIndex={50}
      justifyContent={{ base: "flex-start", md: "center" }}
      color="#fff"
      pt="20px"
      pb={isScrolled ? "12px" : "0"}
      bg={
        isScrolled
          ? useDarkScrollBg
            ? "#0A1628"
            : "rgba(255, 255, 255, 0.62)"
          : "transparent"
      }
      backdropFilter={
        isScrolled && !useDarkScrollBg ? "blur(40px) saturate(1.25)" : "none"
      }
      transition="background 0.2s ease, backdrop-filter 0.2s ease, padding 0.2s ease"
      boxShadow={isScrolled ? "0 1px 0 rgba(226, 232, 240, 0.12)" : "none"}
    >
      <MobileMenu isOpen={isOpen} onClose={onClose} />
      <Box
        w="100%"
        maxW={contentMaxW}
        pl={{ base: "16px", md: "58px" }}
        pr={{ base: "16px", md: "57px" }}
      >
        <Flex
          display={{ base: "flex", md: "none" }}
          align="center"
          w="full"
          justifyContent="space-between"
        >
          <Box>
            <Image
              src="/assets/BizFlex-Logo.svg"
              w="96.67px"
              h="29px"
              objectFit="contain"
              onClick={() => navigate("/")}
              display={useLightNav ? "none" : "flex"}
            />
            <Image
              src="/assets/dark-logo.png"
              w="96.67px"
              h="29px"
              objectFit="contain"
              onClick={() => navigate("/")}
              display={useLightNav ? "flex" : "none"}
            />
          </Box>
          <Box>
            <Image
              src="/assets/menu.svg"
              w="20.96px"
              onClick={onOpen}
              h="14.41px"
              objectFit="contain"
              display={useLightNav ? "flex" : "none"}
            />
            <Box>
              <Box display={useLightNav ? "none" : "flex"}>
                <RiMenu3Fill color="#222425" onClick={onOpen} size="23px" />
              </Box>
            </Box>
          </Box>
        </Flex>

        <Flex
          align="center"
          display={{ base: "none", md: "flex" }}
          w="full"
          position="relative"
        >
          <Flex flex="1" align="center">
            <Image
              src="/assets/dark-logo.png"
              w="160px"
              h="34px"
              cursor="pointer"
              onClick={() => navigate("/")}
              objectFit="contain"
              display={useLightNav ? "flex" : "none"}
            />
            <Image
              src="/assets/BizFlex-Logo.svg"
              w="160px"
              h="34px"
              objectFit="contain"
              onClick={() => navigate("/")}
              display={useLightNav ? "none" : "flex"}
            />
          </Flex>

          <Flex
            align="center"
            gap="40px"
            position="absolute"
            left="50%"
            transform="translateX(-50%)"
            zIndex={20}
          >
            {headerRoutes.map((item) => renderNavItem(item))}
          </Flex>

          <Flex
            flex="1"
            align="center"
            justify="flex-end"
            gap="5px"
            position="relative"
            zIndex={1}
          >
            <a
              href={loginUrl}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                bg="transparent"
                h="48px"
                borderRadius="12px"
                w="130px"
                transition=".3s ease-in-out"
                _hover={{ bg: "#fff", color: "dark" }}
                color="orangeBg"
                fontWeight={600}
              >
                Log in
              </Button>
            </a>
            <a
              href={registerUrl}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                borderRadius="8px"
                h="40px"
                w="130px"
                transition=".3s ease-in-out"
                fontWeight={400}
                {...brandGradientButtonStyles}
              >
                Get Started
              </Button>
            </a>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default Header;
